// src/pages/Event/EventsDashboard.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import EventAnalysisModal from "@/components/event/EventAnalysisModal";
import {
  Plus,
  CalendarDays,
  ClipboardList,
  Users,
  ArrowRight,
  Loader2,
  Sparkles,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Pencil,
  UtensilsCrossed,
  PackageSearch,
  Search,
  Filter,
  ChevronRight,
  ChevronLeft,
  Eye,
  Clock3,
  MapPin,
  BarChart3,
} from "lucide-react";

// ───────────────── helpers ─────────────────
const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const moneyGBP = (v) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(num(v, 0));

const lc = (s) => String(s || "").toLowerCase();

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

async function mapLimit(items, limit, mapper) {
  const out = new Array(items.length);
  let idx = 0;

  async function worker() {
    while (idx < items.length) {
      const i = idx++;
      try {
        out[i] = await mapper(items[i], i);
      } catch (e) {
        out[i] = e;
      }
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    worker(),
  );
  await Promise.all(workers);
  return out;
}

function pad2(v) {
  return String(v).padStart(2, "0");
}

function formatLocalDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function extractDateOnly(value) {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalDateKey(value);
  }

  const raw = String(value).trim();
  if (!raw) return "";

  const directDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (directDateMatch) return directDateMatch[0];

  const leadingDateMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (leadingDateMatch) {
    return `${leadingDateMatch[1]}-${leadingDateMatch[2]}-${leadingDateMatch[3]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return formatLocalDateKey(parsed);
  }

  return raw.slice(0, 10);
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, delta) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + delta);
  return d;
}

function startOfWeek(date) {
  const d = startOfDay(date);
  const weekday = (d.getDay() + 6) % 7;
  return addDays(d, -weekday);
}

function endOfWeek(date) {
  return addDays(startOfWeek(date), 6);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function formatMonthTitle(date) {
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDayTitle(date) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatWeekTitle(date) {
  const start = startOfWeek(date);
  const end = endOfWeek(date);

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${new Intl.DateTimeFormat(
      "en-GB",
      {
        month: "short",
        year: "numeric",
      },
    ).format(start)}`;
  }

  if (!sameMonth && sameYear) {
    return `${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
    }).format(start)} – ${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(end)}`;
  }

  return `${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(start)} – ${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(end)}`;
}

function buildMonthGrid(anchorDate) {
  const first = startOfMonth(anchorDate);
  const firstWeekday = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -firstWeekday);

  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

function buildWeekGrid(anchorDate) {
  const first = startOfWeek(anchorDate);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

function formatTimeRange(eventRow) {
  return eventRow?.kickoff_time
    ? String(eventRow.kickoff_time).slice(0, 5)
    : "Time TBC";
}

function shiftViewDate(date, mode, delta) {
  if (mode === "day") return addDays(date, delta);
  if (mode === "week") return addDays(date, delta * 7);
  return addMonths(date, delta);
}

function resetViewDate(mode) {
  const today = new Date();
  if (mode === "month") return startOfMonth(today);
  return startOfDay(today);
}

function pickEventId(row) {
  return row?.event_id ?? row?.id ?? row?.eventId ?? row?.eventID ?? null;
}

function normalizeEventDateFromRow(row) {
  const explicitDate = row?.event_date ?? row?.date;
  if (explicitDate != null && String(explicitDate).trim() !== "") {
    return extractDateOnly(explicitDate);
  }

  const fallbackDateTime = row?.starts_at ?? row?.startsAt;
  return extractDateOnly(fallbackDateTime);
}

function normalizeEventRow(row) {
  const id = pickEventId(row);
  if (id == null) return null;

  const plannerId =
    row?.planner_id ?? row?.plannerId ?? row?.planner?.id ?? null;

  return {
    id,
    name: row?.event_name ?? row?.name ?? row?.title ?? `Event #${id}`,
    date: normalizeEventDateFromRow(row),
    kickoff_time: row?.kickoff_time ?? row?.start_time ?? "",
    expected_guests: row?.expected_guests ?? row?.guests ?? "",
    planner: row?.planner_name ?? row?.planner ?? row?.plannerName ?? "",
    planner_id: plannerId,
    venue: row?.venue_name ?? row?.venue ?? "",
    raw: row,
  };
}

function rowsFromResponse(data) {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  return [];
}

async function fetchRecentEventsSafe() {
  const tries = [
    async () => api.get("/event", { params: { limit: 80 } }),
    async () => api.get("/event/events", { params: { limit: 80 } }),
    async () => api.get("/events", { params: { limit: 80 } }),
    async () => api.get("/events/events", { params: { limit: 80 } }),
  ];

  let lastErr = null;

  for (const t of tries) {
    try {
      const r = await t();
      return rowsFromResponse(r.data);
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      if (status === 401 || status === 403) throw e;
    }
  }

  throw lastErr || new Error("Failed to load events");
}

// ───────────────── Details normalization ─────────────────
function looksLikeEvent(obj) {
  if (!obj || typeof obj !== "object") return false;
  return (
    obj.event_name != null ||
    obj.event_date != null ||
    obj.starts_at != null ||
    obj.expected_guests != null ||
    obj.planner_id != null ||
    obj.venue_name != null
  );
}

function normalizeDetailsResponse(data) {
  const d = data || {};

  const event =
    (looksLikeEvent(d.event) ? d.event : null) ||
    (looksLikeEvent(d.details?.event) ? d.details.event : null) ||
    (looksLikeEvent(d.row) ? d.row : null) ||
    (looksLikeEvent(d) ? d : null) ||
    {};

  const menuItems =
    (Array.isArray(d.menuItems) && d.menuItems) ||
    (Array.isArray(d.menu_items) && d.menu_items) ||
    (Array.isArray(d.menu) && d.menu) ||
    (Array.isArray(d.items) && d.items) ||
    (Array.isArray(d.selectedItems) && d.selectedItems) ||
    [];

  const staffAssignments =
    (Array.isArray(d.staffAssignments) && d.staffAssignments) ||
    (Array.isArray(d.staff_assignments) && d.staff_assignments) ||
    (Array.isArray(d.staff) && d.staff) ||
    (Array.isArray(d.assignments) && d.assignments) ||
    [];

  const forecast =
    d.forecast && typeof d.forecast === "object" ? d.forecast : null;

  const stockCheck = d.stockCheck ?? d.stock_check ?? d.stock ?? null;
  const aiSummary = d.aiSummary ?? d.ai_summary ?? d.insights ?? null;

  return {
    event,
    menuItems,
    staffAssignments,
    forecast,
    stockCheck,
    aiSummary,
    raw: d,
  };
}

function deriveForecast(detailsRaw, fallbackGuests) {
  const {
    event: ev,
    menuItems,
    forecast,
  } = normalizeDetailsResponse(detailsRaw);

  if (forecast) {
    const mic = num(forecast.menu_items_count, menuItems.length);
    const portions =
      forecast.portions != null ? num(forecast.portions, null) : null;
    const guests = num(ev?.expected_guests ?? fallbackGuests ?? 0, 0) || null;

    const clientTotal =
      forecast.client_total != null
        ? num(forecast.client_total, null)
        : forecast.clientTotal != null
          ? num(forecast.clientTotal, null)
          : null;

    const marginPct =
      forecast.margin_pct != null
        ? num(forecast.margin_pct, null)
        : forecast.margin != null
          ? num(forecast.margin, null)
          : null;

    const isCalculated =
      mic > 0 ||
      num(forecast.cogs_estimated, 0) > 0 ||
      num(forecast.staffing_cost, 0) > 0;

    const safeMargin =
      !isCalculated &&
      mic === 0 &&
      num(forecast.cogs_estimated, 0) === 0 &&
      num(forecast.staffing_cost, 0) === 0
        ? null
        : marginPct;

    return {
      menuItemsCount: mic,
      guests,
      portions: portions != null ? portions : guests,
      gross: clientTotal,
      margin: safeMargin,
      hasRevenue: Boolean(isCalculated),
      note:
        mic === 0
          ? "No menu items detected yet. Portions fall back to guests."
          : null,
    };
  }

  const portionsSum = (Array.isArray(menuItems) ? menuItems : []).reduce(
    (a, m) => {
      return (
        a +
        num(
          m?.expected_servings ??
            m?.expectedServings ??
            m?.portions ??
            m?.portion_qty ??
            m?.portionQty ??
            m?.qty ??
            m?.quantity ??
            0,
          0,
        )
      );
    },
    0,
  );

  const guests = num(ev?.expected_guests ?? fallbackGuests ?? 0, 0) || null;

  return {
    menuItemsCount: menuItems.length,
    guests,
    portions: portionsSum > 0 ? portionsSum : guests,
    gross: null,
    margin: null,
    hasRevenue: false,
    note: null,
  };
}

function deriveReadiness(detailsRaw, eventRowFallback) {
  const {
    event: ev,
    menuItems,
    staffAssignments,
  } = normalizeDetailsResponse(detailsRaw);

  const fallbackPlannerId =
    eventRowFallback?.planner_id ?? eventRowFallback?.raw?.planner_id ?? null;

  const hasPlanner =
    Boolean(ev?.planner) ||
    Boolean(ev?.planner_id ?? ev?.plannerId ?? fallbackPlannerId) ||
    Boolean(ev?.planner_name);

  const hasMenu = Array.isArray(menuItems) && menuItems.length > 0;
  const hasStaffing =
    Array.isArray(staffAssignments) && staffAssignments.length > 0;

  const blockers = [];
  if (!hasPlanner) blockers.push("Planner");
  if (!hasMenu) blockers.push("Menu");
  if (!hasStaffing) blockers.push("Staffing");

  return {
    hasPlanner,
    hasMenu,
    hasStaffing,
    blockers,
    isReady: blockers.length === 0,
  };
}

// ───────────────── UI atoms ─────────────────
function QuickActionCard({
  title,
  subtitle,
  icon: Icon,
  to,
  actionLabel = "Open",
  tone = "sky",
}) {
  const toneMap = {
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    fuchsia: "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700",
  };

  return (
    <Link
      to={to}
      className="group block rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <div
          className={classNames(
            "rounded-2xl border p-2.5",
            toneMap[tone] || toneMap.sky,
          )}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold text-slate-900">
                {title}
              </h3>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                {subtitle}
              </p>
            </div>

            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition group-hover:border-slate-300 group-hover:bg-slate-100">
              {actionLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function Pill({ tone = "neutral", icon: Icon, children, className = "" }) {
  const toneClasses =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : tone === "bad"
          ? "border-rose-200 bg-rose-50 text-rose-800"
          : "border-slate-200 bg-white text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold",
        toneClasses,
        className,
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </span>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function ActionButton({ onClick, icon: Icon, label, tone = "neutral", title }) {
  const toneClasses =
    tone === "primary"
      ? "border-sky-600 bg-sky-600 text-white hover:bg-sky-700"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
        : tone === "intelligence"
          ? "border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";

  return (
    <button
      onClick={onClick}
      title={title}
      type="button"
      className={classNames(
        "rounded-2xl border px-3 py-3 text-left shadow-sm transition",
        toneClasses,
      )}
    >
      <div className="flex items-center gap-2 text-xs font-semibold">
        <Icon className="h-4 w-4" />
        {label}
      </div>
    </button>
  );
}

function getCalendarCardTone(readiness, hasDetail) {
  if (!hasDetail) {
    return {
      wrapper:
        "border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/40",
      badge: "text-slate-600",
      dot: "bg-slate-400",
    };
  }

  if (readiness?.isReady) {
    return {
      wrapper: "border-emerald-300 bg-emerald-50/80 hover:bg-emerald-50",
      badge: "text-emerald-700",
      dot: "bg-emerald-500",
    };
  }

  return {
    wrapper: "border-amber-300 bg-amber-50/80 hover:bg-amber-50",
    badge: "text-amber-700",
    dot: "bg-amber-500",
  };
}

function getPeriodRange(anchorDate, mode) {
  if (mode === "day") {
    const start = startOfDay(anchorDate);
    return { start, end: start };
  }

  if (mode === "week") {
    return { start: startOfWeek(anchorDate), end: endOfWeek(anchorDate) };
  }

  return { start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) };
}

function formatRangeTitle(anchorDate, mode) {
  if (mode === "day") return formatDayTitle(anchorDate);
  if (mode === "week") return formatWeekTitle(anchorDate);
  return formatMonthTitle(anchorDate);
}

// ───────────────── main ─────────────────
export default function EventsDashboard() {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [planners, setPlanners] = useState([]);
  const [detailsById, setDetailsById] = useState({});
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loadError, setLoadError] = useState(null);
  const [viewMode, setViewMode] = useState("month");
  const [viewDate, setViewDate] = useState(() => startOfMonth(new Date()));
  const [analysisEventId, setAnalysisEventId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const highlightedIdRef = useRef(null);

  const plannerById = useMemo(() => {
    const m = new Map();
    for (const p of planners || []) {
      const id = p.id ?? p.planner_id ?? p.staff_id ?? null;
      if (id == null) continue;
      const label =
        p.planner_name || p.full_name || p.email || `Planner #${id}`;
      m.set(String(id), label);
    }
    return m;
  }, [planners]);

  useEffect(() => {
    const msg = location?.state?.toast;
    const openId = location?.state?.openId;
    if (msg) toast.success(msg);
    if (openId != null) highlightedIdRef.current = String(openId);
  }, [location?.state]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const [evRows, pl] = await Promise.all([
          fetchRecentEventsSafe(),
          api
            .get("/event/planners")
            .catch(() => api.get("/planners"))
            .catch(() => ({ data: [] })),
        ]);

        if (!alive) return;

        const normalized = (Array.isArray(evRows) ? evRows : [])
          .map(normalizeEventRow)
          .filter(Boolean);

        normalized.sort((a, b) => {
          const dateCmp = String(b.date || "").localeCompare(
            String(a.date || ""),
          );
          if (dateCmp !== 0) return dateCmp;
          return String(a.kickoff_time || "").localeCompare(
            String(b.kickoff_time || ""),
          );
        });

        setEvents(normalized);
        setPlanners(
          Array.isArray(pl.data)
            ? pl.data
            : pl.data?.rows || pl.data?.items || [],
        );

        const top = normalized.slice(0, 36);

        setDetailsById((prev) => {
          const next = { ...prev };
          for (const e of top) {
            if (!next[e.id])
              next[e.id] = { loading: true, data: null, error: null };
          }
          return next;
        });

        await mapLimit(top, 6, async (e) => {
          try {
            const res = await api.get(`/event/${e.id}/details`);
            if (!alive) return;
            setDetailsById((prev) => ({
              ...prev,
              [e.id]: { loading: false, data: res.data, error: null },
            }));
          } catch (err) {
            if (!alive) return;
            const status = err?.response?.status;
            const msg =
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              (status === 401 || status === 403
                ? "Unauthorized (session/token missing)"
                : "Failed to load details");

            setDetailsById((prev) => ({
              ...prev,
              [e.id]: { loading: false, data: null, error: msg },
            }));
          }
        });
      } catch (err) {
        if (!alive) return;

        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load events";

        setLoadError(msg);

        if (status === 401 || status === 403) {
          toast.error("Session missing or expired. Please login again.");
        } else {
          toast.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const today = formatLocalDateKey(new Date());

    const passTab = (e) => {
      if (tab === "all") return true;
      const d = e.date || "";
      if (!d) return tab === "all";
      if (tab === "upcoming") return d >= today;
      if (tab === "past") return d < today;
      return true;
    };

    const passQuery = (e) => {
      if (!query) return true;
      return [
        e.name,
        e.date,
        e.planner,
        e.venue,
        String(e.expected_guests || ""),
      ].some((x) => lc(x).includes(query));
    };

    return events.filter((e) => passTab(e) && passQuery(e));
  }, [events, q, tab]);

  const visibleRange = useMemo(
    () => getPeriodRange(viewDate, viewMode),
    [viewDate, viewMode],
  );

  const calendarEvents = useMemo(() => {
    const startKey = formatLocalDateKey(visibleRange.start);
    const endKey = formatLocalDateKey(visibleRange.end);

    return filtered.filter((e) => {
      const key = e.date;
      return key && key >= startKey && key <= endKey;
    });
  }, [filtered, visibleRange]);

  const eventsByDate = useMemo(() => {
    const map = new Map();
    for (const e of calendarEvents) {
      const key = e.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }

    for (const [, list] of map) {
      list.sort((a, b) =>
        String(a.kickoff_time || "").localeCompare(
          String(b.kickoff_time || ""),
        ),
      );
    }

    return map;
  }, [calendarEvents]);

  const calendarCells = useMemo(() => {
    if (viewMode === "day") return [startOfDay(viewDate)];
    if (viewMode === "week") return buildWeekGrid(viewDate);
    return buildMonthGrid(viewDate);
  }, [viewDate, viewMode]);

  const agendaEvents = useMemo(() => {
    return [...calendarEvents].sort((a, b) => {
      const dateCmp = String(a.date || "").localeCompare(String(b.date || ""));
      if (dateCmp !== 0) return dateCmp;
      return String(a.kickoff_time || "").localeCompare(
        String(b.kickoff_time || ""),
      );
    });
  }, [calendarEvents]);

  const kpis = useMemo(() => {
    const enriched = filtered
      .map((e) => ({ e, d: detailsById[e.id]?.data || null }))
      .filter((x) => x.d);

    const totalForecast = enriched.reduce((a, x) => {
      const f = deriveForecast(x.d, x.e.expected_guests);
      return a + (f.gross != null ? num(f.gross, 0) : 0);
    }, 0);

    const risk = enriched.reduce(
      (a, x) => a + (deriveReadiness(x.d, x.e).isReady ? 0 : 1),
      0,
    );

    return {
      count: filtered.length,
      rangeCount: calendarEvents.length,
      enrichedCount: enriched.length,
      totalForecast,
      risk,
    };
  }, [filtered, calendarEvents, detailsById]);

  useEffect(() => {
    const id = highlightedIdRef.current;
    if (!id) return;

    const el = document.getElementById(`event-card-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      highlightedIdRef.current = null;
    }
  }, [filtered, detailsById]);

  const openEvent = (id) => navigate(`/events/${id}`);
  const editEvent = (id) => navigate(`/events/${id}/edit`);
  const openMenu = (id) => navigate(`/events/${id}/menu`);
  const stockCheck = (id) => navigate(`/events/${id}/menu?action=stockcheck`);
  const openSingleEventReport = (id) =>
    navigate(`/events/reports?eventId=${id}`);

  const openEventAnalysis = useCallback((id) => {
    const eventIdNumber = Number(id);
    if (!Number.isInteger(eventIdNumber) || eventIdNumber <= 0) {
      toast.error("A valid event is required before analysis can be opened.");
      return;
    }
    setAnalysisEventId(eventIdNumber);
  }, []);

  const closeEventAnalysis = useCallback(() => {
    setAnalysisEventId(null);
  }, []);

  const periodTitle = formatRangeTitle(viewDate, viewMode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-white px-6 py-8 text-slate-900">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Event Operations Dashboard
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Calendar-first event visibility with readiness, forecast,
              intelligence, and next actions.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/events/new"
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" />
              New Event
            </Link>

            <Link
              to="/planners/new"
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-sky-700 shadow-sm transition hover:bg-sky-50"
            >
              <Users className="h-4 w-4" />
              New Planner
            </Link>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <QuickActionCard
            title="Create Event"
            subtitle="Build a new event plan."
            icon={CalendarDays}
            to="/events/new"
            actionLabel="Create"
            tone="emerald"
          />
          <QuickActionCard
            title="Event Reports"
            subtitle="Open live reports and exports."
            icon={BarChart3}
            to="/events/reports"
            actionLabel="Open"
            tone="fuchsia"
          />
          <QuickActionCard
            title="Event History"
            subtitle="Review and compare events."
            icon={ClipboardList}
            to="/events"
            actionLabel="Browse"
            tone="violet"
          />
          <QuickActionCard
            title="Planners"
            subtitle="Manage planner records."
            icon={Users}
            to="/planners"
            actionLabel="Manage"
            tone="sky"
          />
        </div>

        <section className="mb-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              <Pill tone="neutral" icon={Sparkles}>
                {kpis.count} filtered events
              </Pill>

              <Pill tone="neutral" icon={CalendarDays}>
                {kpis.rangeCount} in {periodTitle}
              </Pill>

              <Pill
                tone={kpis.risk ? "warn" : "good"}
                icon={kpis.risk ? AlertTriangle : CheckCircle2}
              >
                {kpis.enrichedCount
                  ? `${kpis.risk} need attention`
                  : "Readiness loading"}
              </Pill>

              <Pill tone="neutral" icon={TrendingUp}>
                Forecast{" "}
                {kpis.enrichedCount ? moneyGBP(kpis.totalForecast) : "—"}
              </Pill>
            </div>

            <div className="text-xs text-slate-500">
              Calendar view • readiness signals • event intelligence
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-xl">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by event name, date, planner, venue, or guests"
                  className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="inline-flex self-start overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                <button
                  onClick={() => setTab("all")}
                  type="button"
                  className={classNames(
                    "inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition",
                    tab === "all"
                      ? "bg-white text-slate-900"
                      : "text-slate-600 hover:bg-white",
                  )}
                >
                  <Filter className="h-4 w-4" />
                  All
                </button>

                <button
                  onClick={() => setTab("upcoming")}
                  type="button"
                  className={classNames(
                    "px-4 py-3 text-sm font-semibold transition",
                    tab === "upcoming"
                      ? "bg-white text-slate-900"
                      : "text-slate-600 hover:bg-white",
                  )}
                >
                  Upcoming
                </button>

                <button
                  onClick={() => setTab("past")}
                  type="button"
                  className={classNames(
                    "px-4 py-3 text-sm font-semibold transition",
                    tab === "past"
                      ? "bg-white text-slate-900"
                      : "text-slate-600 hover:bg-white",
                  )}
                >
                  Past
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Scope
                  </div>

                  {["day", "week", "month"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => {
                        setViewMode(mode);
                        setViewDate((prev) => {
                          const base = startOfDay(prev);
                          if (mode === "month") return startOfMonth(base);
                          return base;
                        });
                      }}
                      className={classNames(
                        "rounded-2xl border px-4 py-2.5 text-sm font-semibold transition",
                        viewMode === mode
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                      )}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setViewDate((d) => shiftViewDate(d, viewMode, -1))
                    }
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                    title="Previous"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <div className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-center text-base font-bold text-slate-900">
                    {periodTitle}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setViewDate((d) => shiftViewDate(d, viewMode, 1))
                    }
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                    title="Next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setViewDate(resetViewDate(viewMode))}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Today
                  </button>
                </div>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Day, week, and month views use local calendar dates, so saved
                events stay on the intended day.
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 pt-5 text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading events...
            </div>
          ) : loadError ? (
            <div className="pt-5 text-rose-700">
              {loadError}
              <div className="mt-2 text-xs text-slate-500">
                If this says unauthorized, the session or token likely needs
                refreshing.
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="pt-5 text-slate-600">
              No events match your current filters.
            </div>
          ) : null}
        </section>

        {!loading && !loadError ? (
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Calendar View
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Scan events by {viewMode}, venue, readiness, and actions.
                </p>
              </div>

              <div className="hidden items-center gap-2 text-xs md:flex">
                <Pill tone="good">Ready</Pill>
                <Pill tone="warn">Needs attention</Pill>
                <Pill tone="neutral">Basic / loading</Pill>
              </div>
            </div>

            {viewMode === "month" || viewMode === "week" ? (
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (label) => (
                    <div
                      key={label}
                      className="border-r border-slate-200 px-3 py-3 text-center text-sm font-semibold text-slate-600 last:border-r-0"
                    >
                      {label}
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                {formatDayTitle(viewDate)}
              </div>
            )}

            <div
              className={classNames(
                "grid",
                viewMode === "day"
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-7",
              )}
            >
              {calendarCells.map((cellDate) => {
                const inMonth =
                  viewMode !== "month" || isSameMonth(cellDate, viewDate);
                const key = formatLocalDateKey(cellDate);
                const dayEvents = eventsByDate.get(key) || [];

                return (
                  <div
                    key={key}
                    className={classNames(
                      "min-h-[180px] border-b border-r border-slate-200 p-2.5",
                      viewMode === "day" && "min-h-[320px] border-r-0",
                      !inMonth && "bg-slate-50/60",
                      inMonth && "bg-white",
                    )}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <div
                        className={classNames(
                          "inline-flex items-center justify-center rounded-full text-sm font-bold",
                          viewMode === "day"
                            ? "bg-slate-100 px-3 py-1 text-slate-900"
                            : inMonth
                              ? "h-8 w-8 bg-slate-100 text-slate-900"
                              : "h-8 w-8 bg-transparent text-slate-400",
                        )}
                      >
                        {viewMode === "day"
                          ? new Intl.DateTimeFormat("en-GB", {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                            }).format(cellDate)
                          : cellDate.getDate()}
                      </div>

                      {dayEvents.length ? (
                        <span className="text-[11px] font-semibold text-slate-500">
                          {dayEvents.length} event
                          {dayEvents.length === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      {dayEvents
                        .slice(0, viewMode === "day" ? 12 : 4)
                        .map((e) => {
                          const state = detailsById[e.id] || {
                            loading: true,
                            data: null,
                            error: null,
                          };
                          const details = state.data;
                          const readiness = details
                            ? deriveReadiness(details, e)
                            : null;
                          const forecast = details
                            ? deriveForecast(details, e.expected_guests)
                            : null;
                          const tone = getCalendarCardTone(
                            readiness,
                            Boolean(details),
                          );
                          const venueLabel = e.venue || "Venue TBC";

                          return (
                            <button
                              key={e.id}
                              id={`event-card-${e.id}`}
                              type="button"
                              onClick={() => openEvent(e.id)}
                              className={classNames(
                                "w-full rounded-2xl border p-3 text-left shadow-sm transition",
                                tone.wrapper,
                              )}
                              title={`Open ${e.name}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-bold text-slate-900">
                                    {e.name}
                                  </div>
                                  <div className="truncate text-xs text-slate-600">
                                    {venueLabel}
                                  </div>
                                </div>

                                <div
                                  className={classNames(
                                    "shrink-0 text-[11px] font-semibold",
                                    tone.badge,
                                  )}
                                >
                                  {state.loading
                                    ? "…"
                                    : readiness?.isReady
                                      ? "100%"
                                      : readiness
                                        ? `${Math.max(0, 100 - readiness.blockers.length * 25)}%`
                                        : "—"}
                                </div>
                              </div>

                              <div className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                                <Clock3 className="h-3.5 w-3.5" />
                                <span>{formatTimeRange(e)}</span>
                              </div>

                              <div className="mt-2 flex items-center justify-between gap-2">
                                <div className="inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/70 px-2 py-1 text-[11px] font-semibold text-slate-700">
                                  <span
                                    className={classNames(
                                      "h-2 w-2 rounded-full",
                                      tone.dot,
                                    )}
                                  />
                                  {state.loading
                                    ? "Loading"
                                    : readiness?.isReady
                                      ? "Ready"
                                      : readiness
                                        ? `Needs ${readiness.blockers.join(", ")}`
                                        : "Basic"}
                                </div>

                                <div className="text-[11px] font-semibold text-slate-700">
                                  {forecast?.gross != null
                                    ? moneyGBP(forecast.gross)
                                    : "—"}
                                </div>
                              </div>
                            </button>
                          );
                        })}

                      {viewMode !== "day" && dayEvents.length > 4 ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
                          +{dayEvents.length - 4} more event
                          {dayEvents.length - 4 === 1 ? "" : "s"}
                        </div>
                      ) : null}

                      {dayEvents.length === 0 && viewMode === "day" ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                          No events scheduled for this day.
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {!loading && !loadError && agendaEvents.length > 0 ? (
          <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {periodTitle} Event Board
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Detailed action view for events in the selected {viewMode}.
                </p>
              </div>

              <div className="text-xs text-slate-500">
                {agendaEvents.length} event
                {agendaEvents.length === 1 ? "" : "s"} in view
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
              {agendaEvents.map((e) => {
                const state = detailsById[e.id] || {
                  loading: true,
                  data: null,
                  error: null,
                };
                const details = state.data;

                const forecast = details
                  ? deriveForecast(details, e.expected_guests)
                  : null;
                const readiness = details ? deriveReadiness(details, e) : null;

                const dateLabel = e.date || "—";
                const guestsLabel =
                  e.expected_guests !== ""
                    ? e.expected_guests
                    : details
                      ? (normalizeDetailsResponse(details).event
                          ?.expected_guests ?? "—")
                      : "—";

                const detailsEv = details
                  ? normalizeDetailsResponse(details).event
                  : null;
                const plannerId =
                  e.planner_id ??
                  detailsEv?.planner_id ??
                  detailsEv?.plannerId ??
                  null;

                const plannerLabel =
                  e.planner ||
                  (plannerId != null
                    ? plannerById.get(String(plannerId))
                    : "") ||
                  detailsEv?.planner_name ||
                  detailsEv?.planner?.planner_name ||
                  detailsEv?.planner?.full_name ||
                  detailsEv?.planner ||
                  (plannerId != null ? `Planner #${plannerId}` : "—");

                const blockers = readiness?.blockers || [];

                return (
                  <div
                    key={e.id}
                    className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => openEvent(e.id)}
                            type="button"
                            className="text-left text-xl font-bold text-slate-900 transition hover:text-sky-700"
                            title="Open event"
                          >
                            {e.name}
                          </button>

                          {readiness ? (
                            readiness.isReady ? (
                              <Pill tone="good" icon={CheckCircle2}>
                                Ready
                              </Pill>
                            ) : (
                              <Pill tone="warn" icon={AlertTriangle}>
                                Needs {blockers.join(", ")}
                              </Pill>
                            )
                          ) : (
                            <Pill tone="neutral" icon={Loader2}>
                              Loading signals
                            </Pill>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {dateLabel}
                          </span>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <Clock3 className="h-3.5 w-3.5" />
                            {formatTimeRange(e)}
                          </span>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            Guests: {guestsLabel}
                          </span>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {e.venue || "Venue TBC"}
                          </span>
                          <span>•</span>
                          <span className="truncate">
                            Planner: {plannerLabel}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => openEvent(e.id)}
                          type="button"
                          className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-white"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Open
                        </button>

                        <button
                          onClick={() => editEvent(e.id)}
                          type="button"
                          className="inline-flex items-center gap-1 rounded-2xl bg-sky-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-sky-700"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>

                        <button
                          onClick={() => openSingleEventReport(e.id)}
                          type="button"
                          className="inline-flex items-center gap-1 rounded-2xl border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-xs font-semibold text-fuchsia-700 transition hover:bg-fuchsia-100"
                        >
                          <BarChart3 className="h-3.5 w-3.5" />
                          Report
                        </button>

                        <button
                          onClick={() => openEventAnalysis(e.id)}
                          type="button"
                          className="inline-flex items-center gap-1 rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-800 transition hover:bg-violet-100"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          Analysis
                        </button>
                      </div>
                    </div>

                    <div className="space-y-5 pt-5">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <TrendingUp className="h-4 w-4 text-emerald-700" />
                            Forecast Snapshot
                          </div>

                          {forecast?.hasRevenue ? (
                            <Pill tone="good" icon={Sparkles}>
                              Calculated
                            </Pill>
                          ) : (
                            <Pill tone="neutral" icon={Sparkles}>
                              Basic
                            </Pill>
                          )}
                        </div>

                        {state.loading ? (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Building forecast...
                          </div>
                        ) : state.error ? (
                          <div className="text-sm text-rose-700">
                            {state.error}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                            <StatTile
                              label="Portions"
                              value={forecast?.portions ?? "—"}
                            />
                            <StatTile
                              label="Guests"
                              value={forecast?.guests ?? guestsLabel ?? "—"}
                            />
                            <StatTile
                              label="Client Total"
                              value={
                                forecast?.gross != null
                                  ? moneyGBP(forecast.gross)
                                  : "—"
                              }
                            />
                            <StatTile
                              label="Margin"
                              value={
                                forecast?.margin != null
                                  ? `${num(forecast.margin).toFixed(1)}%`
                                  : "—"
                              }
                            />
                          </div>
                        )}

                        {forecast?.note ? (
                          <div className="mt-3 text-xs text-slate-500">
                            {forecast.note}
                          </div>
                        ) : null}
                      </div>

                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <ActionButton
                          onClick={() => openMenu(e.id)}
                          icon={UtensilsCrossed}
                          label="Menu"
                          title="Menu selection"
                        />

                        <ActionButton
                          onClick={() => stockCheck(e.id)}
                          icon={PackageSearch}
                          label="Stock Check"
                          title="Check stock"
                        />

                        <ActionButton
                          onClick={() => editEvent(e.id)}
                          icon={Pencil}
                          label="Edit Event"
                          title="Edit event"
                        />

                        <ActionButton
                          onClick={() => openSingleEventReport(e.id)}
                          icon={BarChart3}
                          label="Report"
                          tone="primary"
                          title="Open single event report"
                        />

                        <ActionButton
                          onClick={() => openEventAnalysis(e.id)}
                          icon={Sparkles}
                          label="Analysis"
                          tone="intelligence"
                          title="Open SOLO event analysis"
                        />

                        <ActionButton
                          onClick={() => openEvent(e.id)}
                          icon={ArrowRight}
                          label="Full View"
                          tone="success"
                          title="Open event command center"
                        />
                      </div>

                      {details
                        ? (() => {
                            const nd = normalizeDetailsResponse(details);
                            return nd.aiSummary ? (
                              <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4">
                                <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                                  <Sparkles className="h-4 w-4 text-sky-700" />
                                  Operational Insights
                                </div>
                                <div className="text-sm text-slate-700">
                                  {typeof nd.aiSummary === "string"
                                    ? nd.aiSummary
                                    : JSON.stringify(nd.aiSummary)}
                                </div>
                              </div>
                            ) : null;
                          })()
                        : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        <section className="mt-10 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Recent Planners
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Quick access to your latest planner records.
              </p>
            </div>

            <Link
              to="/planners"
              className="text-sm font-semibold text-sky-700 transition hover:text-sky-800"
            >
              Manage planners
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 pt-5 text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading planners...
            </div>
          ) : planners.length === 0 ? (
            <div className="pt-5 text-slate-600">
              No planners found yet. Add one to start assigning events.
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-200">
              {planners.slice(0, 6).map((p) => (
                <li
                  key={p.planner_id ?? p.id}
                  className="py-3 text-sm text-slate-700"
                >
                  {p.planner_name ||
                    p.full_name ||
                    p.email ||
                    `Planner #${p.planner_id ?? p.id}`}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <EventAnalysisModal
        open={analysisEventId != null}
        eventId={analysisEventId}
        onClose={closeEventAnalysis}
      />
    </div>
  );
}
